import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Send, Leaf, Clock, Package } from "lucide-react";
import api from "../lib/api";

const EMPTY = { name: "", email: "", order_number: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [sentTo, setSentTo] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    setError("");
    try {
      await api.post("/contact", form);
      setSentTo({ name: form.name.split(" ")[0], email: form.email });
      setForm(EMPTY);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Something went wrong. Please check your details and try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-8 py-16 lg:py-24 grid lg:grid-cols-5 gap-12 lg:gap-20" data-testid="contact-page">
      <aside className="lg:col-span-2">
        <p className="eyebrow mb-3">Say hello</p>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[var(--ink-forest)] tracking-tight leading-[1.05]">
          We'd love to <span className="italic text-[var(--terracotta)]">hear from you</span>
        </h1>
        <p className="text-[var(--muted-fg)] mt-5 leading-relaxed">
          A question about an oil, a gift idea, or something about your order — write to us here. A real person reads every note.
        </p>
        <ul className="mt-10 space-y-5 text-sm text-[var(--ink-forest)]">
          <ContactPoint icon={Clock} title="Quick replies" text="We answer within one business day." />
          <ContactPoint icon={Package} title="About an order?" text="Add your order number (DC-XXXXXX) so we can help faster." />
          <ContactPoint icon={Leaf} title="Just browsing?" text={<>Our <Link to="/safety" className="text-[var(--terracotta)] underline">safety guide</Link> answers many oil questions.</>} />
        </ul>
      </aside>

      <section className="lg:col-span-3">
        {sentTo ? (
          <ThankYou sentTo={sentTo} onAgain={() => setSentTo(null)} />
        ) : (
          <form onSubmit={submit} className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-3xl p-6 sm:p-10 grid sm:grid-cols-2 gap-5" data-testid="contact-form">
            <Label text="Your name">
              <input required value={form.name} onChange={set("name")} className="ct-input" placeholder="Jane Doe" data-testid="contact-name" />
            </Label>
            <Label text="Email">
              <input required type="email" value={form.email} onChange={set("email")} className="ct-input" placeholder="you@example.com" data-testid="contact-email" />
            </Label>
            <Label text="Order number (optional)" full>
              <input value={form.order_number} onChange={set("order_number")} className="ct-input font-mono" placeholder="DC-XXXXXX" data-testid="contact-order-number" />
            </Label>
            <Label text="Your message" full>
              <textarea required rows={6} value={form.message} onChange={set("message")} className="ct-input resize-y" placeholder="How can we help?" data-testid="contact-message" />
            </Label>
            {error && (
              <p className="sm:col-span-2 bg-[var(--muted)] text-[var(--ink-forest)] rounded-xl p-3 text-sm" data-testid="contact-error">{error}</p>
            )}
            <button
              type="submit"
              disabled={sending}
              className="sm:col-span-2 bg-[var(--terracotta)] text-white rounded-full py-3.5 text-sm font-semibold hover:bg-[var(--terracotta-deep)] transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2"
              data-testid="contact-submit-btn"
            >
              <Send className="w-4 h-4" />
              {sending ? "Sending…" : "Send message"}
            </button>
          </form>
        )}
      </section>

      <style>{`
        .ct-input { width: 100%; background: var(--bg-cream); border: 1px solid var(--border); border-radius: 12px; padding: 0.875rem 1rem; font-size: 0.9rem; color: var(--ink-forest); outline: none; transition: border-color .2s; }
        .ct-input:focus { border-color: var(--terracotta); }
      `}</style>
    </div>
  );
}

function ContactPoint({ icon: Icon, title, text }) {
  return (
    <li className="flex gap-4">
      <span className="w-10 h-10 rounded-full bg-[var(--terracotta)]/10 flex items-center justify-center shrink-0">
        <Icon className="w-4 h-4 text-[var(--terracotta)]" />
      </span>
      <div>
        <p className="font-serif text-lg leading-tight">{title}</p>
        <p className="text-[var(--muted-fg)] mt-0.5">{text}</p>
      </div>
    </li>
  );
}

function Label({ text, full, children }) {
  return (
    <label className={`block ${full ? "sm:col-span-2" : ""}`}>
      <span className="eyebrow block mb-2">{text}</span>
      {children}
    </label>
  );
}

function ThankYou({ sentTo, onAgain }) {
  return (
    <div className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-3xl p-8 sm:p-12 text-center" data-testid="contact-success">
      <span className="w-14 h-14 rounded-full bg-[var(--terracotta)]/10 flex items-center justify-center mx-auto mb-5">
        <Leaf className="w-6 h-6 text-[var(--terracotta)]" />
      </span>
      <h2 className="font-serif text-3xl text-[var(--ink-forest)]">Thank you, {sentTo.name}!</h2>
      <p className="text-[var(--muted-fg)] mt-3 max-w-md mx-auto leading-relaxed">
        Your message is on its way to us. We've sent a copy to <strong className="text-[var(--ink-forest)]">{sentTo.email}</strong> and will reply within one business day.
      </p>
      <div className="mt-8 flex flex-wrap gap-3 justify-center">
        <Link to="/shop" className="bg-[var(--terracotta)] text-white rounded-full px-6 py-3 text-sm font-semibold hover:bg-[var(--terracotta-deep)] transition-colors" data-testid="contact-success-shop-link">
          Keep browsing
        </Link>
        <button onClick={onAgain} className="border border-[var(--border)] text-[var(--ink-forest)] rounded-full px-6 py-3 text-sm font-semibold hover:border-[var(--ink-forest)]/30 transition-colors" data-testid="contact-send-another-btn">
          Send another note
        </button>
      </div>
    </div>
  );
}
